import { useRef } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { AtomModalApi } from '@/components/Atoms/AtomModal/useAtomModal'

import type {
  ImportFileFormat,
  ImportSourceType,
} from '../../types/stockImports.types'
import {
  type ConfigFileFormat,
  type ConfigFormInput,
  type ConfigFormValues,
  type ConfigSourceType,
  DEFAULT_CRON_EXPRESSION,
  fromMapperValues,
  hasSecondStepData,
  SECOND_STEP_RESET_VALUES,
} from '../lib/configFormModel'
import { FILE_FORMAT_OPTIONS } from '../lib/configModalTexts'
import { normalizeFileMaskByFormat } from '../lib/configRequest'
import type { ExcelMapperValues } from '../UrlExcelMapper/UrlExcelMapper.types'
import { clearUrlExcelMapperCache } from '../UrlExcelMapper/urlExcelMapperCache'

const SOURCE_TYPES: readonly ImportSourceType[] = [
  'EMAIL',
  'URL',
  'MANUAL',
  'CUSTOM_IMAP',
]

const isSourceType = (value: string): value is ImportSourceType =>
  SOURCE_TYPES.some((sourceType) => sourceType === value)

const isFileFormat = (value: string): value is ImportFileFormat =>
  FILE_FORMAT_OPTIONS.some((option) => option.value === value)

const SOURCE_STEP_FIELDS: Record<
  ConfigSourceType,
  Array<keyof ConfigFormInput>
> = {
  '': [],
  URL: ['url'],
  EMAIL: ['expectedSender', 'subjectMask', 'fileMask'],
  CUSTOM_IMAP: ['imapLogin', 'imapPasswordEncrypted', 'useSsl', 'fileMask'],
  MANUAL: [],
}

const MAPPER_FIELDS = [
  'startRowIndex',
  'endRowIndex',
  'articleColumnNumber',
  'articleInchColumnNumber',
  'barcodeColumnNumber',
  'descriptionColumnNumber',
  'brandColumnNumber',
  'quantityColumnNumber',
  'sheetNames',
  'sheetIndexes',
] as const

const SOURCE_RESET_CONTENT =
  'Настройки второго шага зависят от конкретного файла и будут очищены: диапазон строк и листы, номера колонок, фильтры брендов, бренд по умолчанию, правила остатков и загруженная таблица выбора колонок. «Отмена» оставит прежний источник.'

type UseConfigFieldHandlersParams = {
  form: UseFormReturn<ConfigFormInput, unknown, ConfigFormValues>
  atomModal: AtomModalApi
  isSelfSupplierMode: boolean
}

export const useConfigFieldHandlers = ({
  form,
  atomModal,
  isSelfSupplierMode,
}: UseConfigFieldHandlersParams) => {
  const { getValues, setValue, reset } = form
  const lastSourceTypeRef = useRef(getValues('sourceType'))
  const dirty = { shouldDirty: true }

  const normalizeFileMask = (fileFormat: ConfigFileFormat) => {
    const { sourceType, fileMask } = getValues()
    if (!fileFormat) return
    if (sourceType !== 'EMAIL' && sourceType !== 'CUSTOM_IMAP') return
    const normalized = normalizeFileMaskByFormat(fileMask, fileFormat)
    if (normalized && normalized !== fileMask)
      setValue('fileMask', normalized, dirty)
  }

  const handleSourceTypeChange = async (value: string) => {
    const previous = lastSourceTypeRef.current
    if (!isSourceType(value) || value === previous) return

    if (hasSecondStepData({ ...getValues(), sourceType: previous })) {
      const confirmed = await atomModal.confirm({
        title: 'Изменить источник?',
        content: SOURCE_RESET_CONTENT,
        okText: 'Да, сбросить',
        danger: true,
      }).result
      if (!confirmed) {
        setValue('sourceType', previous, dirty)
        return
      }
      clearUrlExcelMapperCache(getValues('url'))
      reset(
        { ...getValues(), ...SECOND_STEP_RESET_VALUES },
        { keepDefaultValues: true }
      )
    }

    lastSourceTypeRef.current = value
    setValue(
      'cronExpression',
      value === 'EMAIL'
        ? ''
        : getValues('cronExpression') || DEFAULT_CRON_EXPRESSION,
      dirty
    )
  }

  const handleSupplierChange = () => {
    if (isSelfSupplierMode) return
    setValue('storeId', '', dirty)
  }

  const handleFileFormatChange = (value: string) => {
    if (!isFileFormat(value)) return
    if (getValues('fileFormat') !== value)
      setValue('fileFormat', value, { ...dirty, shouldValidate: true })
    normalizeFileMask(value)
  }

  const handleMappingChange = (values: ExcelMapperValues) => {
    const next = fromMapperValues(values)
    MAPPER_FIELDS.forEach((field) => {
      const value = next[field]
      if (value !== undefined) setValue(field, value, dirty)
    })
    if (next.sheetSelectionType)
      setValue('sheetSelectionType', next.sheetSelectionType, dirty)
  }

  return {
    sourceStepFields: (sourceType: ConfigSourceType) =>
      SOURCE_STEP_FIELDS[sourceType],
    handleSourceTypeChange,
    handleSupplierChange,
    handleFileFormatChange,
    handleFileMaskBlur: () => normalizeFileMask(getValues('fileFormat')),
    handleMappingChange,
    handleUseQuantityHeader: (header: string) =>
      setValue('expectedQuantityHeader', header, dirty),
  }
}
